/**
 * One transient message at a time, with an optional action — the "Undo" after
 * a bulk tick. Deliberately a single slot: two overlapping undos would race
 * each other for the same rows, and a queue would keep showing messages for
 * things the user has already moved past.
 */

import { create } from 'zustand';

const DEFAULT_MS = 6_000;

export interface Toast {
  /** Bumped per show, so a repeat of the same text still remounts. */
  id: number;
  message: string;
  action?: { label: string; run: () => void };
}

interface ToastState {
  toast: Toast | null;
  show: (toast: Omit<Toast, 'id'>, ms?: number) => void;
  dismiss: () => void;
}

export const useToast = create<ToastState>((set, get) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;

  const dismiss = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (get().toast) set({ toast: null });
  };

  return {
    toast: null,
    show: (toast, ms = DEFAULT_MS) => {
      if (timer) clearTimeout(timer);
      seq += 1;
      set({ toast: { ...toast, id: seq } });
      timer = setTimeout(dismiss, ms);
    },
    dismiss,
  };
});
